import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    forwardRef,
    HostListener,
} from '@angular/core';
import {
    FormControl,
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
    debounceTime,
    distinctUntilChanged,
    startWith,
    Subject,
    takeUntil,
} from 'rxjs';
import { ToasterService } from 'src/app/core/services/toaster/toaster.service';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { MatAutocomplete } from '@angular/material/autocomplete';

@Component({
    selector: 'app-chip-input',
    templateUrl: './chip-input.component.html',
    styleUrls: ['./chip-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipInputComponent),
            multi: true,
        },
    ],
})
export class ChipInputComponent implements OnInit, ControlValueAccessor {
    @ViewChild('input') input: ElementRef<HTMLInputElement>;
    @ViewChild('auto') auto: MatAutocomplete;
    @Input() suggestions: string[] = [];
    @Input() separatorKeysCodes: number[] = [ENTER, COMMA, SPACE];
    @Input() formControl = new FormControl([]);
    @Input() showLoader: boolean = false;
    @Input() label: string = 'Enter email addresses';
    @Input() placeholder: string = 'Add email addresses...';
    @Input() maxLimit: number | null = null;
    @Input() studentEmail:any = null; // Used for student email validation

    @Output() addItem = new EventEmitter<string>();
    @Output() removeItem = new EventEmitter<number>();
    @Output() onSearch = new EventEmitter<any>();

    inputFilterControl: FormControl = new FormControl('');
    private _unsubscribeAll: Subject<any> = new Subject();
    private emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    constructor(private _toasterService: ToasterService) { }

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent) {
        event.preventDefault();

        // Get pasted content
        const pastedInput = event.clipboardData?.getData('text/plain');
        if (!pastedInput) return;

        // Process the pasted emails
        this.processEmails(pastedInput);
    }

    private processEmails(input: string) {
        const emails = input
            .split(/[\s,]+/) // Split by spaces or commas
            .map(email => this.cleanEmail(email))
            .filter(email => email.length > 0);

        emails.forEach(email => {
            if (this.maxLimit !== null && this.formControl.value.length >= this.maxLimit) {
                this._toasterService.showWarnig(
                    'Limit Reached',
                    `You can only add up to ${this.maxLimit} email addresses.`
                );
                return;
            }

            if(this.studentEmail === email){
            this._toasterService.showWarnig(
                'Invalid Email',
                `You cannot add student email address.`
            );
            return;
            }

            if (this.isValidEmail(email)) {
                if (!this.formControl.value.includes(email)) {
                    const currentEmails = [...this.formControl.value];
                    currentEmails.push(email);
                    this.formControl.setValue(currentEmails);
                    this.addItem.emit(email);
                    this.formControl.markAsDirty();
                    this.formControl.markAsTouched();
                } else {
                    this._toasterService.showWarnig(
                        'Exists!',
                        `${email} This email already exists.`
                    );
                }
            } else {
                this._toasterService.showWarnig(
                    'Invalid Email',
                    `${email} is not a valid email address.`
                );
            }
        });

        this.input.nativeElement.value = '';
        this.inputFilterControl.setValue('');
    }

    writeValue(value: any): void { }
    registerOnChange(fn: any): void { }
    registerOnTouched(fn: any): void { }
    setDisabledState?(isDisabled: boolean): void { }

    ngOnInit(): void {
        this.inputFilterControl.valueChanges
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                takeUntil(this._unsubscribeAll),
                startWith('')
            )
            .subscribe((res: string) => {
                this.onSearch.emit({
                    target: {
                        value: this.cleanEmail(res),
                    },
                });
            });
    }

    private cleanEmail(email: string): string {
        return email ? email.trim().replace(/\s+/g, '') : '';
    }

    private isValidEmail(email: string): boolean {
        return this.emailRegex.test(email);
    }

    add(event: any): void {
        const inputValue = this.cleanEmail(event.value);

        if (!inputValue) return;
        if(this.studentEmail === inputValue){
            this._toasterService.showWarnig(
                'Invalid Email',
                `You cannot add student email address.`
            );
            return;
        }

        if (this.maxLimit !== null && this.formControl.value.length >= this.maxLimit) {
            this._toasterService.showWarnig(
                'Limit Reached',
                `You can only add up to ${this.maxLimit} email addresses.`
            );
            return;
        }

        if (!this.isValidEmail(inputValue)) {
            this._toasterService.showWarnig(
                'Invalid Email',
                `${inputValue} is not a valid email address.`
            );
            return;
        }

        if (this.formControl.value.includes(inputValue)) {
            this._toasterService.showWarnig(
                'Exists!',
                `${inputValue} This email already exists.`
            );
        } else {
            this.formControl.setValue([...this.formControl.value, inputValue]);
            this.addItem.emit(inputValue);
        }

        this.input.nativeElement.value = '';
        this.inputFilterControl.setValue('');
        this.formControl.markAsDirty();
        this.formControl.markAsTouched();
    }

    remove(index: number): void {
        const currentValue = [...this.formControl.value];
        currentValue.splice(index, 1);
        this.formControl.setValue(currentValue);
        this.formControl.markAsDirty();
        this.formControl.markAsTouched();
        this.removeItem.emit(index);
    }

    addSuggestion(event: any): void {
        const suggestion = this.cleanEmail(event.option.value);

        if (this.maxLimit !== null && this.formControl.value.length >= this.maxLimit) {
            this._toasterService.showWarnig(
                'Limit Reached',
                `You can only add up to ${this.maxLimit} email addresses.`
            );
            return;
        }

        if(this.studentEmail === suggestion){
            this._toasterService.showWarnig(
                'Invalid Email',
                `You cannot add student email address.`
            );
            return;
        }

        if (!this.isValidEmail(suggestion)) {
            this._toasterService.showWarnig(
                'Invalid Email',
                `${suggestion} is not a valid email address.`
            );
            return;
        }

        if (this.formControl.value.includes(suggestion)) {
            this._toasterService.showWarnig(
                'Exists!',
                `${suggestion} This email already exists.`
            );
        } else {
            this.formControl.setValue([...this.formControl.value, suggestion]);
            this.addItem.emit(suggestion);
        }

        this.input.nativeElement.value = '';
        this.inputFilterControl.setValue('');
        this.formControl.markAsDirty();
        this.formControl.markAsTouched();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}